import React, { useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';

import Card from 'components/Card';
import Spinner from 'components/Spinner';
import PricesContainer from 'modules/Prices/components/container';
import EvolutionLineChart from 'modules/Prices/pages/evolution/chart';
import Container, { PriceDiv } from 'modules/Prices/pages/evolution/container';
import EvolutionPriceFilters from 'modules/Prices/pages/evolution/filters';
import PricesEvolutionHeader from 'modules/Prices/pages/evolution/header';
import PricesEvolutionSelectCompetitors from 'modules/Prices/pages/evolution/selectCompetitors';

import {
  dataFilter,
  getLabels,
  getMarketOptions,
  getNameNetwork
} from 'modules/Prices/helpers';
import {
  applyFiltersToData,
  getDataFilters,
  transformDataForChart
} from 'modules/Prices/pages/evolution/helpers';

import { useTypedQuery } from 'apiTyped/helpers/useTypedQuery';
import { useTypedQueries } from 'apiTyped/helpers/useTypedQueries';
import { usePricesFilters } from 'apiTyped/hooks/prices';
import { Wine, MarketOption } from 'interface/index';
import { Competitor, FilterEvolutionPricesOptions } from 'interface/price';

const PricesEvolution = () => {
  // Local state
  const [vintage, setVintage] = useState<string | null>(null);
  const [wine, setWine] = useState<Wine | null>(null);
  const [network, setNetwork] = useState<string | null>(null);
  const [market, setMarket] = useState<MarketOption | null>(null);
  const [currency, setCurrency] = useState<string>('EUR');
  const [competitors, setCompetitors] = useState<Competitor[]>([]);

  // Load filters
  const { isLoadingPricesFilters, isCampaign: showVintages } =
    usePricesFilters();

  const { isLoading: isLoadingEvolPricesFilters, data: evolPricesFilters } =
    useTypedQuery('get', '/pricesV2/evolution/filters', {
      queryOpts: {
        staleTime: 1000 * 60
      }
    });

  // Quick accessor for filter wine data
  const getFilterWineData = (wineId: number) =>
    evolPricesFilters?.wines.find((item) => item.wine_id === wineId);

  // Should be moved to a hook in apiTyped/hooks
  const { isLoading: isLoadingEvolPricesData, data } = useTypedQuery(
    'post',
    '/pricesV2/evolution/data',
    {
      // Request inputs
      body: {
        // @ts-expect-error wine.id is checked through enabled condition
        wine_id: wine?.id,
        // @ts-expect-error vintage is checked through enabled condition
        vintage: vintage,
        // @ts-expect-error wine.id is checked through enabled condition
        rivals_ids: get(getFilterWineData(wine?.id), 'rivals', []).map(
          (item) => item.wine_id
        )
      },
      queryOpts: {
        // Skip if wine or vintage are not set
        enabled: !!(wine?.id && vintage),
        // How long to keep stale data in cache
        staleTime: 1000 * 60 * 2
      }
    }
  );

  // Set initial values for wine and vintage when filters are loaded
  useEffect(() => {
    if (
      !evolPricesFilters ||
      isLoadingEvolPricesFilters ||
      wine ||
      vintage ||
      evolPricesFilters.wines.length === 0
    ) {
      return;
    }
    setWine({
      id: evolPricesFilters.wines[0].wine_id,
      name: evolPricesFilters.wines[0].wine_name
    });
    setVintage(evolPricesFilters.wines[0].vintages[0]);
  }, [evolPricesFilters]);

  const defaultCurrency = useMemo(
    () => get(data, 'chart[0].displayed_currency', 'EUR'),
    [data]
  );

  useEffect(() => setCurrency(defaultCurrency), [data]);

  // Should be moved to a hook in apiTyped/hooks
  const competitorsData = useTypedQueries(
    'post',
    '/pricesV2/evolution/data',
    competitors.map((competitor) => ({
      body: {
        wine_id: competitor.id,
        vintage: competitor.vintage,
        rivals_ids: []
      }
    }))
  )
    .map((result) => result.data)
    .filter((data) => data !== undefined);

  // Possible networks according to data (before applying market filter)
  const possibleNetworksFromData = [
    ...new Set(get(data, 'chart', []).map((item) => item.network_code))
  ];

  // Update networks when printed data changes
  useEffect(() => {
    if (
      !evolPricesFilters ||
      (network && possibleNetworksFromData.includes(network))
    ) {
      return;
    }

    // Eventually change network to show data of graph
    const nextNetwork = evolPricesFilters.networks.find((network) =>
      possibleNetworksFromData.includes(network)
    );
    if (nextNetwork) {
      setNetwork(nextNetwork);
    }
  }, [data, wine, vintage]);

  // Compute options for filters
  const options: FilterEvolutionPricesOptions = {
    wines: evolPricesFilters
      ? evolPricesFilters.wines.map((item) => ({
          value: item.wine_id,
          label: item.wine_name
        }))
      : [],
    vintages: wine
      ? get(getFilterWineData(wine.id), 'vintages', []).map((vintage) => ({
          value: vintage,
          label: vintage
        }))
      : [],
    networks: evolPricesFilters
      ? evolPricesFilters.networks
          .filter((network) => getNameNetwork(network))
          .map((network) => ({
            value: network,
            label: getNameNetwork(network),
            disabled: !possibleNetworksFromData.includes(network)
          }))
      : [],
    markets: getMarketOptions(
      dataFilter(data?.chart || [], getDataFilters({ network }))
    )
  };

  const dataChart =
    !network || !data
      ? []
      : transformDataForChart({
          data: [data, ...competitorsData].map((elem) =>
            applyFiltersToData({
              data: elem ? elem.chart : [],
              network,
              market
            })
          ),
          currency,
          network,
          market
        });

  const labels = getLabels(dataChart, null, true);

  const exportName = [
    network ? getNameNetwork(network) : null,
    market ? market.label : null,
    vintage,
    wine ? wine.name : null
  ]
    .filter((item) => item)
    .join(' - ');
  return (
    <PricesContainer>
      <Container>
        <EvolutionPriceFilters
          wine={wine}
          setWine={setWine}
          vintage={vintage}
          setVintage={setVintage}
          market={market}
          onChangeMarket={(value) => {
            setCompetitors([]);
            setCurrency(
              value && value.currency ? value.currency : defaultCurrency
            );
            setMarket(value);
          }}
          network={network}
          setNetwork={setNetwork}
          options={options}
          getWineData={getFilterWineData}
          disabled={
            isLoadingPricesFilters ||
            isLoadingEvolPricesFilters ||
            isLoadingEvolPricesData
          }
        />
        <PriceDiv>
          <Card id="prices-evolution">
            <PricesEvolutionHeader exportName={exportName} />
            <EvolutionLineChart
              data={dataChart}
              labels={labels}
              currency={currency}
              network={network}
            />
            <PricesEvolutionSelectCompetitors
              competitors={competitors}
              setCompetitors={setCompetitors}
              showVintages={showVintages}
              vintage={vintage}
              rivals={get(data, 'rivals', [])}
            />
          </Card>
          <Spinner
            loading={
              isLoadingPricesFilters ||
              isLoadingEvolPricesFilters ||
              isLoadingEvolPricesData
            }
            little
          />
        </PriceDiv>
      </Container>
    </PricesContainer>
  );
};

export default PricesEvolution;
